import type { FocusEvent } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import type { MultiChoiceValue, QuestionRendererProps } from '../types';

const OTHER_OPTION_ID = '__other__';

const normalizeValue = (value: MultiChoiceValue | undefined): MultiChoiceValue => {
  if (!value) {
    return { optionIds: [], otherText: undefined };
  }
  const uniqueOptionIds = Array.from(new Set(value.optionIds ?? [])).filter(Boolean);
  return {
    optionIds: uniqueOptionIds,
    otherText: value.otherText,
  };
};

const buildSelectionHint = (minSelections?: number | null, maxSelections?: number | null) => {
  if (minSelections && maxSelections) {
    return `Select ${minSelections}-${maxSelections} options`;
  }
  if (minSelections) {
    return `Select at least ${minSelections} option${minSelections > 1 ? 's' : ''}`;
  }
  if (maxSelections) {
    return `Select up to ${maxSelections} option${maxSelections > 1 ? 's' : ''}`;
  }
  return undefined;
};

export const MultipleChoiceMultiQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  mode,
  disabled,
}: QuestionRendererProps<MultiChoiceValue>) => {
  if (question.type !== 'multiple_choice_multi') {
    return null;
  }

  const isInteractive = mode === 'runtime' && !disabled;
  const normalized = normalizeValue(value);
  const otherSelected = normalized.optionIds.includes(OTHER_OPTION_ID);
  const baseSelections = normalized.optionIds.filter((id) => id !== OTHER_OPTION_ID);
  const totalSelections = baseSelections.length + (otherSelected ? 1 : 0);
  const selectionHint = buildSelectionHint(question.minSelections, question.maxSelections);
  const reachedMax = Boolean(question.maxSelections) && totalSelections >= (question.maxSelections ?? 0);
  const otherDisabled = !isInteractive || (!otherSelected && reachedMax);

  const toggleOption = (optionId: string, checked: boolean) => {
    if (!isInteractive) {
      return;
    }

    const withoutOption = normalized.optionIds.filter((id) => id !== optionId);
    if (checked) {
      const nextOptionIds = [...withoutOption, optionId];
      const includesOther = optionId === OTHER_OPTION_ID || withoutOption.includes(OTHER_OPTION_ID);
      const nextBaseSelections = nextOptionIds.filter((id) => id !== OTHER_OPTION_ID).length;
      const nextTotalSelections = nextBaseSelections + (includesOther ? 1 : 0);
      if (question.maxSelections && nextTotalSelections > question.maxSelections) {
        return;
      }
      onChange?.({
        optionIds: nextOptionIds,
        otherText: optionId === OTHER_OPTION_ID ? normalized.otherText ?? '' : normalized.otherText,
      });
      onBlur?.();
    } else {
      onChange?.({ optionIds: withoutOption, otherText: optionId === OTHER_OPTION_ID ? undefined : normalized.otherText });
      onBlur?.();
    }
  };

  const handleOtherChange = (text: string) => {
    if (!isInteractive) {
      return;
    }

    const hasText = text.trim().length > 0;
    const withoutOther = normalized.optionIds.filter((id) => id !== OTHER_OPTION_ID);
    if (hasText) {
      if (!normalized.optionIds.includes(OTHER_OPTION_ID)) {
        const nextTotal = withoutOther.length + 1;
        if (question.maxSelections && nextTotal > question.maxSelections) {
          return;
        }
      }
      onChange?.({ optionIds: [...withoutOther, OTHER_OPTION_ID], otherText: text });
      return;
    }

    onChange?.({ optionIds: withoutOther, otherText: undefined });
  };

  const otherInputId = `${question.id}-other`;

  return (
    <div
      className="space-y-3"
      aria-describedby={`validation-${question.id}`}
      aria-errormessage={error ? `error-${question.id}` : undefined}
      aria-invalid={Boolean(error)}
      onBlur={(event: FocusEvent<HTMLDivElement>) => {
        if (!onBlur) {
          return;
        }
        const nextFocus = event.relatedTarget as Node | null;
        if (nextFocus && event.currentTarget.contains(nextFocus)) {
          return;
        }
        onBlur();
      }}
    >
      {selectionHint ? <p className="text-xs text-muted-foreground">{selectionHint}</p> : null}
      <div className="space-y-2">
        {question.options.map((option) => {
          const optionId = `${question.id}-${option.id}`;
          const selected = normalized.optionIds.includes(option.id);
          const disableOption = !selected && reachedMax;
          const optionInteractive = isInteractive && !disableOption;
          return (
            <Label
              key={option.id}
              htmlFor={optionId}
              className={`flex items-start gap-3 rounded-lg border border-border/60 bg-background px-3 py-2.5 text-inherit font-normal leading-normal ${
                optionInteractive ? 'cursor-pointer hover:border-primary/70' : 'cursor-not-allowed'
              }`}
            >
              <Checkbox
                id={optionId}
                checked={selected}
                onCheckedChange={(checked) => toggleOption(option.id, Boolean(checked))}
                disabled={!isInteractive || disableOption}
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground">{option.label}</span>
                {option.description ? <p className="mt-1 text-xs text-muted-foreground">{option.description}</p> : null}
              </div>
            </Label>
          );
        })}
        {question.allowOther ? (
          <div className="space-y-2 rounded-lg border border-border/60 bg-background px-3 py-2.5">
            <Label
              htmlFor={otherInputId}
              className={`flex items-start gap-3 text-inherit font-normal leading-normal ${
                otherDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <Checkbox
                id={otherInputId}
                checked={otherSelected}
                onCheckedChange={(checked) => toggleOption(OTHER_OPTION_ID, Boolean(checked))}
                disabled={otherDisabled}
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground">Other</span>
                <p className="mt-1 text-xs text-muted-foreground">Let respondents provide their own answer.</p>
              </div>
            </Label>
            <Input
              value={normalized.otherText ?? ''}
              onChange={(event) => handleOtherChange(event.target.value)}
              placeholder="Please specify"
              disabled={otherDisabled}
              aria-describedby={`validation-${question.id}`}
              aria-invalid={Boolean(error)}
            />
          </div>
        ) : null}
      </div>
      {mode === 'runtime' ? (
        <p className="text-xs text-muted-foreground">
          Selected {totalSelections}
          {question.maxSelections ? ` / ${question.maxSelections}` : ''}
        </p>
      ) : null}
    </div>
  );
};
